const baseUrl = import.meta.env.VITE_GEO_ASSETS_BASE_URL?.trim().replace(/\/$/, "");

export interface GeoArtifact {
  id: string;
  type: "pmtiles" | "cog" | "json" | "geojson" | "gpkg";
  path: string;
  visibility: "authenticated";
  bytes: number;
  sha256: string;
  metadata: Record<string, unknown>;
}

export interface GeoManifest {
  schema_version: number;
  version: string;
  generated_at: string;
  artifacts: GeoArtifact[];
}

export function getGeoAssetUrl(path: string) {
  if (!baseUrl) throw new Error("missing-assets-endpoint");
  return `${baseUrl}/${path.replace(/^\/+/, "")}`;
}

function authorizedHeaders(accessToken: string | undefined, extra?: HeadersInit) {
  if (!accessToken) throw new Error("missing-access-token");
  const headers = new Headers(extra);
  headers.set("Authorization", `Bearer ${accessToken}`);
  return headers;
}

export async function fetchGeoManifest(accessToken: string | undefined, signal?: AbortSignal) {
  const response = await fetch(getGeoAssetUrl("current/manifest.json"), {
    headers: authorizedHeaders(accessToken),
    signal,
  });

  if (!response.ok) throw new Error(`manifest-${response.status}`);
  return (await response.json()) as GeoManifest;
}

export async function verifyGeoAssets(accessToken: string | undefined, signal?: AbortSignal) {
  const manifest = await fetchGeoManifest(accessToken, signal);
  const pmtiles = manifest.artifacts.find((artifact) => artifact.type === "pmtiles");
  if (!pmtiles) throw new Error("missing-pmtiles");

  const rangeResponse = await fetch(getGeoAssetUrl(pmtiles.path), {
    headers: authorizedHeaders(accessToken, { Range: "bytes=0-126" }),
    signal,
  });

  if (rangeResponse.status !== 206) throw new Error(`range-${rangeResponse.status}`);
  const header = new Uint8Array(await rangeResponse.arrayBuffer());
  const signature = new TextDecoder().decode(header.slice(0, 7));
  if (signature !== "PMTiles") throw new Error("invalid-pmtiles-header");

  return {
    artifactCount: manifest.artifacts.filter((artifact) => ["pmtiles", "cog", "json"].includes(artifact.type)).length,
    rangeBytes: header.byteLength,
    version: manifest.version,
  };
}

export async function fetchGeoJsonArtifact<T>(
  artifactId: string,
  accessToken: string | undefined,
  signal?: AbortSignal,
) {
  const manifest = await fetchGeoManifest(accessToken, signal);
  const artifact = manifest.artifacts.find((item) => item.id === artifactId && item.type === "json");
  if (!artifact) throw new Error(`missing-json-artifact-${artifactId}`);

  const response = await fetch(getGeoAssetUrl(artifact.path), {
    headers: authorizedHeaders(accessToken),
    signal,
  });
  if (!response.ok) throw new Error(`json-artifact-${response.status}`);
  return {
    data: (await response.json()) as T,
    version: manifest.version,
  };
}